import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { OG_LOCALES, absoluteUrl, splitLangPath } from "../i18n/urls";
import { localizedMeta } from "../i18n/seoMeta";

const SITE_URL = "https://arabellgroup.com";
const DEFAULT_IMAGE = `${SITE_URL}/og-arabell.jpg`;

/**
 * Componente SEO riusabile per ogni pagina.
 * Imposta title, description, keywords, canonical, Open Graph, Twitter Card e
 * opzionalmente uno o più blocchi JSON-LD (structured data).
 */
const SEO = ({
  title,
  description,
  keywords,
  path = "/",
  image = DEFAULT_IMAGE,
  type = "website",
  jsonLd,
  noindex = false,
}) => {
  // La lingua canonica deriva dal prefisso URL (/en, /de, ...), non dal browser
  const { pathname } = useLocation();
  const lang = splitLangPath(pathname).lang || "it";
  // Title/description tradotti per le URL localizzate (evita duplicati con l'italiano)
  const loc = localizedMeta(path, lang);
  const effTitle = loc?.title || title;
  const effDescription = loc?.description || description;
  const fullTitle = effTitle.includes("Arabell") ? effTitle : `${effTitle} | Arabell Group`;
  // Canonical localizzata: italiano senza prefisso, altre lingue con /xx/
  const canonicalUrl = absoluteUrl(path, lang);
  const ldArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={effDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
      )}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={effDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={OG_LOCALES[lang] || "it_IT"} />
      <meta property="og:site_name" content="Arabell Group" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={effDescription} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD */}
      {ldArray.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
